import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import adminAdAccountService, { AdminAdAccountRow, AdPlatform } from '../../services/api/adminAdAccountService';
import {
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskyLabel,
  TaskySelect,
  TaskySpinner,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
} from '../tasky';

const STATUS_LABELS: Record<string, { label: string; variant: 'success' | 'warning' | 'destructive' | 'secondary' }> = {
  connected: { label: '接続中', variant: 'success' },
  syncing: { label: '同期中', variant: 'success' },
  needs_reauth: { label: '再認証が必要', variant: 'warning' },
  error: { label: 'エラー', variant: 'destructive' },
  disconnected: { label: '解除済み', variant: 'secondary' },
};

const PLATFORM_LABELS: Record<AdPlatform, string> = { meta: 'Meta', google: 'Google' };

const formatDate = (value: string | null): string => (value ? new Date(value).toLocaleString('ja-JP') : '-');

/**
 * 全組織の接続済み広告アカウントを参照専用で一覧する。
 * テスト用のリーダーアカウントと Tasky 用アカウントが混ざらないよう、
 * どの組織がどのアカウントを接続しているかを見分けるための画面（変更操作なし）。
 */
const AdAccountOverviewPanel: React.FC = () => {
  const [rows, setRows] = useState<AdminAdAccountRow[]>([]);
  const [total, setTotal] = useState(0);
  const [platform, setPlatform] = useState<AdPlatform | ''>('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminAdAccountService.list({ platform, status });
      setRows(result.rows);
      setTotal(result.total);
    } catch {
      setError('広告アカウントの一覧を取得できませんでした。');
    } finally {
      setLoading(false);
    }
  }, [platform, status]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-dark">広告アカウント</h2>
          <p className="mt-1 text-sm text-gray-500">
            全組織が接続している Meta・Google の広告アカウントです（参照のみ）。接続・解除は各組織のメディア連携設定から行います。
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <TaskyLabel htmlFor="admin-ad-accounts-platform">媒体</TaskyLabel>
            <TaskySelect
              id="admin-ad-accounts-platform"
              value={platform}
              onChange={(event) => setPlatform(event.target.value as AdPlatform | '')}
            >
              <option value="">すべて</option>
              <option value="meta">Meta</option>
              <option value="google">Google</option>
            </TaskySelect>
          </div>
          <div>
            <TaskyLabel htmlFor="admin-ad-accounts-status">状態</TaskyLabel>
            <TaskySelect id="admin-ad-accounts-status" value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">すべて</option>
              {Object.entries(STATUS_LABELS).map(([value, meta]) => (
                <option key={value} value={value}>{meta.label}</option>
              ))}
            </TaskySelect>
          </div>
          <TaskyButton variant="secondary" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className="mr-1.5 h-4 w-4" />
            再読み込み
          </TaskyButton>
        </div>
      </div>

      {error && <TaskyAlert tone="error">{error}</TaskyAlert>}
      {!loading && total > rows.length && (
        <TaskyAlert tone="warning">
          全 {total} 件のうち先頭 {rows.length} 件を表示しています。媒体・状態で絞り込んでください。
        </TaskyAlert>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-8">
          <TaskySpinner size="md" />
          <span className="text-sm text-gray-600">読み込み中...</span>
        </div>
      ) : rows.length === 0 ? (
        <p className="py-8 text-sm text-gray-500">該当する広告アカウントはありません。</p>
      ) : (
        <div className="overflow-x-auto">
          <TaskyTable className="min-w-max">
            <TaskyTableHead>
              <TaskyTableRow hoverable={false}>
                <TaskyTableHeaderCell>組織</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>媒体</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>アカウント</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>状態</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>接続したユーザー</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>接続日時</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>最終同期</TaskyTableHeaderCell>
              </TaskyTableRow>
            </TaskyTableHead>
            <TaskyTableBody>
              {rows.map((row) => {
                const statusMeta = STATUS_LABELS[row.status] ?? { label: row.status, variant: 'secondary' as const };
                return (
                  <TaskyTableRow key={row.id} data-testid="admin-ad-account-row">
                    <TaskyTableCell className="text-sm">
                      {row.organization ? `${row.organization.name}（ID ${row.organization.id}）` : '-'}
                    </TaskyTableCell>
                    <TaskyTableCell className="text-sm">{PLATFORM_LABELS[row.platform] ?? row.platform}</TaskyTableCell>
                    <TaskyTableCell className="text-sm">
                      <div>{row.account_name || '(名称なし)'}</div>
                      <div className="text-xs text-gray-500">
                        ID {row.account_id_suffix || '-'}
                        {row.is_test_account ? '・テストアカウント' : ''}
                      </div>
                    </TaskyTableCell>
                    <TaskyTableCell>
                      <TaskyBadge variant={statusMeta.variant}>{statusMeta.label}</TaskyBadge>
                    </TaskyTableCell>
                    <TaskyTableCell className="text-sm">{row.connected_by || '-'}</TaskyTableCell>
                    <TaskyTableCell className="whitespace-nowrap text-sm text-gray-600">{formatDate(row.connected_at)}</TaskyTableCell>
                    <TaskyTableCell className="whitespace-nowrap text-sm text-gray-600">{formatDate(row.last_synced_at)}</TaskyTableCell>
                  </TaskyTableRow>
                );
              })}
            </TaskyTableBody>
          </TaskyTable>
        </div>
      )}
    </div>
  );
};

export default AdAccountOverviewPanel;
